import { z } from 'zod';
import { calibrationOutcomeSchema, macroVisualDigestSchema, macroVisualProfileSchema, pixelRegionSchema, calibrationSampleSchema, normalizedVisualScoreSchema, scoredCalibrationSampleSchema,
  VISUAL_COMPARISON_LIMIT, VISUAL_SPATIAL_DENOMINATOR } from './common';
import { blobAssetSchema, sourceImageAssetSchema, type BlobAsset } from './jobs';
import { canonicalJson, canonicalJsonBytes, sha256Hex } from '../macroPackage/canonicalJson';
import { sourceGraphIdentitySchema, type SourceGraphIdentity } from '../macroPackage/workflow';
import { calibrationEvidenceReferenceWindowSchema, validateCalibrationEvidenceReferenceWindow,
  type CalibrationEvidenceReferenceWindow } from './evidencePagesReferenceWindow';
export { calibrationEvidenceReferenceWindowSchema, type CalibrationEvidenceReferenceWindow } from './evidencePagesReferenceWindow';

// jobs -> macroTree -> historical evidence is cyclic. Leaf references must resolve after initialization.
const evidenceBlobSchema = z.lazy(() => blobAssetSchema);
const evidenceSourceIdentitySchema = z.lazy(() => sourceGraphIdentitySchema);

export const CALIBRATION_EVIDENCE_PAGE_SIZE = 128;
export const CALIBRATION_EVIDENCE_FRAME_LIMIT = 64;
export const CALIBRATION_EVIDENCE_RADIUS_US = 100_000;
export const CALIBRATION_EVIDENCE_SCORING_RULE = 'raw-max-exact-inliers-10-v2' as const;

export const calibrationEvidenceBindingIdentitySchema = z.object({
  nodeId: z.string().min(1).max(128), packageId: z.string().uuid(),
  regionId: z.string().uuid(), referenceId: z.string().min(1).max(128),
}).strict();
export type CalibrationEvidenceBindingIdentity = z.infer<typeof calibrationEvidenceBindingIdentitySchema>;

/** Tuple order uses UTF-16 code units, never locale-dependent collation. */
export function compareCalibrationEvidenceBindings(left: CalibrationEvidenceBindingIdentity,
  right: CalibrationEvidenceBindingIdentity): number {
  for (const key of ['nodeId', 'packageId', 'regionId', 'referenceId'] as const) {
    if (left[key] !== right[key]) return left[key] < right[key] ? -1 : 1;
  }
  return 0;
}

/** Content-addressed keys are safe relative paths; grants supply storage access separately. */
export function calibrationEvidenceAssetSchema(kind: 'reports' | 'pages' | 'images' | 'descriptors') {
  const extension = kind === 'images' ? 'png' : kind === 'descriptors' ? 'bin' : 'json';
  const contentType = kind === 'images' ? 'image/png' : kind === 'descriptors' ? 'application/octet-stream' : 'application/json';
  return evidenceBlobSchema.superRefine((asset, context) => {
    if (asset.objectKey !== `evidence/${kind}/${asset.digest}.${extension}` || asset.contentType !== contentType) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Evidence asset requires its exact content-addressed key and media type' });
    }
  });
}
export const calibrationEvidenceIndexAssetSchema = evidenceBlobSchema.superRefine((asset, context) => {
  if (asset.objectKey !== 'evidence/index.json' || asset.contentType !== 'application/json') {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Evidence index requires evidence/index.json and application/json' });
  }
});
export const calibrationEvidenceImageSchema = calibrationEvidenceAssetSchema('images');
/** Exact original capture PNGs only; crops and rendered/descriptor evidence use their smaller contracts. */
export const calibrationEvidenceSourceImageSchema = z.lazy(() => sourceImageAssetSchema).superRefine((asset, context) => {
  if (asset.objectKey !== `evidence/images/${asset.digest}.png`) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source evidence requires its exact content-addressed image key' });
  }
});
export const calibrationEvidenceDescriptorSchema = calibrationEvidenceAssetSchema('descriptors');
const thresholdPercent = z.number().int().min(0).max(100);
const offsetUs = z.number().int().safe();
const currentEvidencePolicy = { profile: macroVisualProfileSchema,
  scoringRule: z.literal(CALIBRATION_EVIDENCE_SCORING_RULE), radiusMs: z.literal(100), outlierPercent: z.literal(10) };

export const regionCalibrationEvidenceReportSchema = z.object({
  version: z.literal(2), sourceIdentity: evidenceSourceIdentitySchema,
  ...calibrationEvidenceBindingIdentitySchema.shape, ...currentEvidencePolicy,
  targetId: z.string().min(1).max(128), region: pixelRegionSchema, presetDigest: macroVisualDigestSchema,
  referenceWindow: calibrationEvidenceReferenceWindowSchema.optional(),
  frames: z.array(z.object({ id: z.string().min(1).max(128), tUs: z.number().int().nonnegative().safe(),
    offsetUs, fullFrame: calibrationEvidenceSourceImageSchema, crop: calibrationEvidenceImageSchema,
  }).strict()).min(1).max(CALIBRATION_EVIDENCE_FRAME_LIMIT),
  omittedOffsetsUs: z.array(offsetUs),
  sampleCount: z.number().int().positive().max(VISUAL_COMPARISON_LIMIT),
  samplePages: z.array(calibrationEvidenceAssetSchema('pages')).min(1)
    .max(VISUAL_COMPARISON_LIMIT / CALIBRATION_EVIDENCE_PAGE_SIZE),
  outcome: calibrationOutcomeSchema, recommendedThresholdPercent: thresholdPercent,
}).strict().superRefine((report, context) => {
  validateCalibrationEvidenceReferenceWindow(report, context);
  const target = report.frames.find(frame => frame.id === report.targetId);
  if (new Set(report.frames.map(frame => frame.id)).size !== report.frames.length
    || new Set(report.frames.map(frame => frame.tUs)).size !== report.frames.length
    || target?.offsetUs !== 0 || report.frames.filter(frame => frame.offsetUs === 0).length !== 1
    || report.frames.some(frame => !target || frame.tUs - target.tUs !== frame.offsetUs)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'],
      message: 'Evidence requires unique actual frames, one target and exact absolute microsecond offsets' });
  }
  if (new Set(report.omittedOffsetsUs).size !== report.omittedOffsetsUs.length
    || report.omittedOffsetsUs.some(offset => offset === 0 || report.frames.some(frame => frame.offsetUs === offset))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['omittedOffsetsUs'], message: 'Omissions must uniquely name unavailable non-anchor offsets' });
  }
  if (report.samplePages.length !== Math.ceil(report.sampleCount / CALIBRATION_EVIDENCE_PAGE_SIZE)
    || new Set(report.samplePages.map(page => page.objectKey)).size !== report.samplePages.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['samplePages'], message: 'Evidence must pin every distinct sample page' });
  }
  if (report.outcome.readiness === 'ready'
    && report.outcome.recommendedThresholdPercent !== report.recommendedThresholdPercent) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['recommendedThresholdPercent'], message: 'Recommendation must match the native outcome' });
  }
});
export type RegionCalibrationEvidenceReport = z.infer<typeof regionCalibrationEvidenceReportSchema>;

export const calibrationEvidenceIndexSchema = z.object({
  version: z.literal(1), sourceIdentity: evidenceSourceIdentitySchema, ...currentEvidencePolicy,
  entries: z.array(z.object({ ...calibrationEvidenceBindingIdentitySchema.shape,
    recommendedThresholdPercent: thresholdPercent, report: calibrationEvidenceAssetSchema('reports'),
    payloads: z.array(z.union([calibrationEvidenceSourceImageSchema, calibrationEvidenceDescriptorSchema])),
  }).strict()).max(16_384),
  digest: macroVisualDigestSchema,
}).strict().superRefine((index, context) => {
  for (let ordinal = 0; ordinal < index.entries.length; ordinal++) {
    const entry = index.entries[ordinal];
    if (ordinal > 0 && compareCalibrationEvidenceBindings(index.entries[ordinal - 1], entry) >= 0) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['entries', ordinal], message: 'Evidence bindings must be sorted and unique' });
    }
    if (new Set(entry.payloads.map(asset => asset.objectKey)).size !== entry.payloads.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['entries', ordinal, 'payloads'], message: 'Payload pins must be unique' });
    }
  }
});
export type CalibrationEvidenceIndex = z.infer<typeof calibrationEvidenceIndexSchema>;

/** The certificate digest excludes only the index's own digest field. Asset pins hash full bytes. */
export async function calibrationEvidenceIndexDigest(index: Omit<CalibrationEvidenceIndex, 'digest'>): Promise<string> {
  const { digest: _digest, ...body } = index as CalibrationEvidenceIndex;
  return sha256Hex(canonicalJsonBytes(body));
}

export const calibrationEvidenceSampleIdentitySchema = scoredCalibrationSampleSchema.omit({ raw: true }).strict();
export type CalibrationEvidenceSampleIdentity = z.infer<typeof calibrationEvidenceSampleIdentitySchema>;
export interface CalibrationEvidenceSampleExpectation extends CalibrationEvidenceSampleIdentity {
  /** Native materiality classifies authored material transforms from measured pixels. */
  acceptedLabels?: readonly CalibrationEvidenceSampleIdentity['label'][];
  /** Work-plan identity retained for verifier-only temporal disposition checks. */
  candidateReferenceId?: string;
  /** Present only for an actual frame captured with the raw recording. */
  capturedRole?: 'temporal' | 'negative';
}
const descriptorBytes = { hash256: 32, rgb64: 12_288, rgb128: 49_152, detail: 3072, frequency: 3072 } as const;
const descriptorPayloadSchema = z.object({
  stage: z.enum(['hash256', 'rgb64', 'rgb128', 'detail', 'frequency']), asset: calibrationEvidenceDescriptorSchema,
}).strict().superRefine((payload, context) => {
  if (payload.asset.byteCount !== descriptorBytes[payload.stage]) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['asset', 'byteCount'], message: 'Descriptor length must match the native profile stage' });
  }
});
export const calibrationEvidenceRowSchema = z.object({
  ordinal: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT - 1), sample: calibrationSampleSchema,
  normalized: normalizedVisualScoreSchema.nullable(),
  disposition: z.enum(['included', 'excluded-temporal-outlier', 'excluded-policy', 'abstained']),
  reasonCode: z.string().min(1).max(128).nullable(),
  transform: z.object({ id: z.string().min(1).max(128),
    parameters: z.record(z.union([z.number().finite(), z.string(), z.boolean()])),
  }).strict(),
  inputImages: z.array(calibrationEvidenceImageSchema).min(1), outputImages: z.array(calibrationEvidenceImageSchema),
  descriptorPayloads: z.array(descriptorPayloadSchema),
}).strict().superRefine((row, context) => {
  const issue = (message: string) => context.addIssue({ code: z.ZodIssueCode.custom, message });
  if (row.transform.id !== row.sample.transformId) issue('Transform must match the canonical sample identity');
  try { canonicalJson(row.transform); } catch { issue('Transform parameters must be finite canonical JSON values'); }
  if ((row.disposition === 'included') !== (row.reasonCode === null)) issue('Every exclusion or abstention requires a reason');
  if (row.disposition === 'abstained') {
    const error = 'errorCode' in row.sample ? row.sample.errorCode
      : row.normalized?.status === 'abstain' ? row.normalized.errorCode : undefined;
    if (!error || row.reasonCode !== error || row.normalized?.status === 'scored'
      || (row.normalized?.status === 'abstain' && row.normalized.errorCode !== error)) issue('Abstention must retain the native error');
  } else if (!('raw' in row.sample) || row.normalized?.status === 'abstain') {
    issue('Only scored comparisons may be included or excluded');
  }
  if (row.disposition === 'included' && (row.normalized?.status !== 'scored' || row.normalized.forcedMismatch
    || row.sample.split !== 'training' || !['benign', 'noop'].includes(row.sample.label))) issue('Only eligible training positives may widen the threshold');
  if (row.disposition === 'excluded-temporal-outlier' && (!['temporal-neighbor', 'identity'].includes(row.sample.transformId)
    || row.sample.label !== 'benign' || row.sample.split !== 'training' || row.normalized?.status !== 'scored')) issue('Temporal outliers require a scored captured neighbor');
  if ('raw' in row.sample) {
    if (row.sample.raw.detailStructureLost && (row.disposition === 'included'
      || (row.normalized?.status === 'scored' && row.normalized.forcedMismatch !== 'structure_lost'))) {
      issue('Structure-lost measurements require the native hard gate and can never be included');
    }
    if (row.normalized?.status === 'scored' && row.normalized.forcedMismatch
      && row.normalized.differenceNumerator !== row.normalized.differenceDenominator) issue('A forced mismatch must retain the canonical rejection score');
    const required = ['hash256', 'rgb64', 'rgb128',
      ...(row.sample.raw.detailNumerator === undefined ? [] : ['detail']),
      ...(row.sample.raw.frequencyNumerator === undefined ? [] : ['frequency'])];
    if (required.some(stage => !row.descriptorPayloads.some(payload => payload.stage === stage))) issue('Scored comparisons must retain native descriptor stages');
  }
  if (new Set(row.descriptorPayloads.map(payload => `${payload.stage}:${payload.asset.objectKey}`)).size !== row.descriptorPayloads.length) issue('Descriptor pins must be unique within a stage');
});
export type CalibrationEvidenceRow = z.infer<typeof calibrationEvidenceRowSchema>;

export const calibrationEvidencePageSchema = z.object({
  version: z.literal(1), nodeId: z.string().min(1).max(128), regionId: z.string().uuid(),
  referenceId: z.string().min(1).max(128),
  pageIndex: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT / CALIBRATION_EVIDENCE_PAGE_SIZE - 1),
  rows: z.array(calibrationEvidenceRowSchema).min(1).max(CALIBRATION_EVIDENCE_PAGE_SIZE),
}).strict().superRefine((page, context) => {
  if (page.rows.some((row, ordinal) => row.ordinal !== page.pageIndex * CALIBRATION_EVIDENCE_PAGE_SIZE + ordinal)
    || new Set(page.rows.map(row => row.sample.caseId)).size !== page.rows.length
    || page.rows.some(row => row.sample.referenceId !== page.referenceId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['rows'], message: 'Page rows must cover consecutive unique sample identities for their reference' });
  }
});
export type CalibrationEvidencePage = z.infer<typeof calibrationEvidencePageSchema>;

/** Bounded blobs are read one at a time; the validator never retains the closure's image bytes. */
export type CalibrationEvidenceAssetReader = (asset: BlobAsset) => Promise<Uint8Array>;
export interface CalibrationEvidenceBindingExpectation extends CalibrationEvidenceBindingIdentity {
  /** Complete native schedule in report order, including negatives and abstentions. */
  samples: Iterable<CalibrationEvidenceSampleExpectation>;
  referenceWindow?: CalibrationEvidenceReferenceWindow;
  expectedFramePins?: readonly Pick<RegionCalibrationEvidenceReport['frames'][number], 'id' | 'tUs' | 'offsetUs' | 'fullFrame'>[];
}
export interface CalibrationEvidenceExpectation {
  sourceIdentity: SourceGraphIdentity;
  bindings: readonly CalibrationEvidenceBindingExpectation[];
}
export { CalibrationEvidenceValidationError, validateCalibrationEvidenceClosure,
  validateCalibrationEvidencePlaybackMetadata, type CalibrationEvidencePlaybackMetadata } from './evidenceClosureValidation';
